import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Button } from 'reactstrap'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import PageLayout from '../components/PageLayout'
import { useApi } from '../hooks/useApi'
import { useForm } from '../hooks/useForm'
import eventBus from '../utils/eventBus'

/**
 * Scrolls to and focuses the first field with an error. Field ids are the
 * field names (FormField sets id={name}); pseudo-keys (e.g. "_experience")
 * can point at any element with that id.
 */
const focusFirstError = (errors) => {
  const first = Object.keys(errors).find((k) => errors[k])
  if (!first) return
  const el = document.getElementById(first) || document.getElementById(first.replace(/^_/, ''))
  if (!el) return
  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  if (typeof el.focus === 'function') el.focus({ preventScroll: true })
}

const StandardPage = ({
  title,
  breadcrumbItem,
  fetchData,
  submitData,
  initialFormData = {},
  renderForm,
  renderContent,
  showActions = true,
  customActions,
  onDataLoaded,
  onSaved,
  transformData,
  validate
}) => {
  const { t } = useTranslation()
  const [validationMessage, setValidationMessage] = useState(null)
  const [hasLoaded, setHasLoaded] = useState(!fetchData)

  useEffect(() => {
    document.title = `${t(breadcrumbItem)} | ACIG Guapiaçu Cidadão`
  }, [breadcrumbItem, t])

  const fetchApi = useApi()
  const submitApi = useApi()
  const form = useForm(initialFormData)

  // Warn before the tab is closed/reloaded with unsaved edits.
  useEffect(() => {
    if (!form.isDirty) return undefined
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [form.isDirty])

  const applyFetched = (data) => {
    const transformedData = transformData ? transformData(data) : data
    form.setInitialValues(transformedData)
    return transformedData
  }

  useEffect(() => {
    if (fetchData) {
      fetchApi.execute(fetchData, {
        onSuccess: (data) => {
          const transformed = applyFetched(data)
          onDataLoaded?.(transformed)
          setHasLoaded(true)
        },
        onError: () => setHasLoaded(true)
      })
    }
  }, [])

  const handleSubmit = async () => {
    if (!submitData) return
    setValidationMessage(null)

    // Client-side validation gate: nothing leaves the browser while there are
    // errors, and the user is taken to the first one.
    if (validate) {
      const errors = validate(form.values) || {}
      const hasErrors = Object.values(errors).some(Boolean)
      form.setErrors(errors)
      if (hasErrors) {
        setValidationMessage(t('Please fix the highlighted fields before saving'))
        focusFirstError(errors)
        return
      }
    }

    await submitApi.execute(() => submitData(form.values), {
      onSuccess: (saved) => {
        eventBus.emit('profile-updated')
        // Toast is visible wherever the user is on the page (the Save button
        // sits at the bottom of long forms; the inline banner is at the top).
        toast.success(t('Changes saved successfully'))
        onSaved?.(saved)
        // Refresh from the server in the background — the form stays mounted.
        if (fetchData) fetchApi.execute(fetchData, { onSuccess: applyFetched })
      },
      onError: (message) => {
        toast.error(message)
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    })
  }

  const defaultActions = showActions ? (
    <>
      <Button color="primary" onClick={handleSubmit} disabled={submitApi.isLoading}>
        {submitApi.isLoading && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />}
        {submitApi.isLoading ? t('Saving...') : t('Save Changes')}
      </Button>
    </>
  ) : null

  const actions = customActions || defaultActions

  return (
    <PageLayout
      title={t(title)}
      breadcrumbItem={t(breadcrumbItem)}
      isLoading={!hasLoaded}
      error={validationMessage || fetchApi.error || submitApi.error}
      success={submitApi.isSuccess ? t('Changes saved successfully') : null}
      actions={actions}
    >
      {renderForm && renderForm({
        ...form,
        setValues: form.setValues,
        t,
        isSubmitting: submitApi.isLoading
      })}

      {renderContent && renderContent({
        data: form.values,
        isLoading: fetchApi.isLoading,
        error: fetchApi.error,
        t
      })}
    </PageLayout>
  )
}

StandardPage.propTypes = {
  title: PropTypes.string.isRequired,
  breadcrumbItem: PropTypes.string.isRequired,
  fetchData: PropTypes.func,
  submitData: PropTypes.func,
  initialFormData: PropTypes.object,
  renderForm: PropTypes.func,
  renderContent: PropTypes.func,
  showActions: PropTypes.bool,
  customActions: PropTypes.node,
  onDataLoaded: PropTypes.func,
  onSaved: PropTypes.func,
  transformData: PropTypes.func,
  /** (values) => { [field]: message } — blocks submit when any message is set */
  validate: PropTypes.func
}

export default StandardPage
